import { EmailOutlined, Google, LockOutlined } from "@mui/icons-material";
import { Box, Button, CircularProgress, Link, Stack } from "@mui/material";
import { TextInput } from "components";
import { ENDPOINTS } from "config/constants";
import { useContextController } from "context";
import { login } from "context/actions";
import { useFetch, useTitle, useTranslation } from "hooks";
import { PublicLayout } from "layouts";
import AuthPageLayout from "pages/auth/AuthPageLayout";
import { authFormLinkSx, authStackSpacing, fieldSx, iconSx, outlinedBtnSx, primaryBtnSx } from "pages/auth/formShared";
import { useSnackbar } from "notistack";
import { FormEventHandler, useCallback, useEffect, useState } from "react";
import { Link as LinkRouter } from "react-router-dom";
import { UserModel } from "types";

const defaultValue = {
  email: "",
  password: ""
};

const defaultState = defaultValue;

interface Data {
  user?: UserModel;
  errors?: typeof defaultState;
}

export default function Login() {
  const [state, setState] = useState(defaultState);
  const [errors, setErrors] = useState(defaultValue);
  const { isLoading, response, error, callFetch: callFetchPost } = useFetch<Data>("POST");
  const [, dispatch] = useContextController();
  const { enqueueSnackbar } = useSnackbar();
  const t = useTranslation();

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      callFetchPost(ENDPOINTS.account, {
        body: JSON.stringify({
          email: state.email,
          password: state.password
        })
      }).catch(console.error);
    },
    [callFetchPost, state]
  );

  useEffect(() => {
    if (response?.data?.user) {
      login(response.data.user, dispatch);
    }
  }, [response, dispatch]);

  useEffect(() => {
    if (error) {
      if (error.data?.errors) {
        setErrors((prevState) => ({
          ...prevState,
          ...error.data?.errors
        }));
      } else {
        enqueueSnackbar(error?.message || JSON.stringify(error), { variant: "error" });
      }
    }
  }, [error, enqueueSnackbar]);

  useTitle("Login");

  return (
    <PublicLayout>
      <AuthPageLayout title={t["Welcome Back"] || "Welcome back"} subtitle={t["Sign in to your account"]}>
        <Box component="form" onSubmit={onSubmit}>
          <Stack spacing={authStackSpacing}>
            <TextInput
              autoFocus
              required
              fullWidth
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              label={t["Email Address"]}
              placeholder={t["e.g johndoes@gmail.com"]}
              helperText={errors?.email}
              error={!!errors?.email}
              value={state.email}
              startAdornment={<EmailOutlined sx={iconSx} />}
              sx={fieldSx}
              onChange={(e) => {
                setState((prevState) => ({ ...prevState, email: e.target.value }));
                setErrors((prevState) => ({ ...prevState, email: "" }));
              }}
            />
            <TextInput
              required
              fullWidth
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              label={t["Password"]}
              placeholder="********"
              helperText={errors?.password}
              error={!!errors?.password}
              startAdornment={<LockOutlined sx={iconSx} />}
              sx={fieldSx}
              onChange={(e) => {
                setState((prevState) => ({ ...prevState, password: e.target.value }));
                setErrors((prevState) => ({ ...prevState, password: "" }));
              }}
            />
            <Box display="flex" justifyContent="flex-end">
              <Link component={LinkRouter} to="/forgot-password" sx={authFormLinkSx}>
                {t["Forgot Password?"]}
              </Link>
            </Box>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              endIcon={isLoading ? <CircularProgress size={18} /> : undefined}
              fullWidth
              sx={primaryBtnSx}
            >
              {t["Sign in"]}
            </Button>
            <Button
              variant="outlined"
              component="a"
              href={ENDPOINTS.google}
              startIcon={<Google />}
              fullWidth
              sx={outlinedBtnSx}
            >
              {t["Sign in with Google"]}
            </Button>
          </Stack>
        </Box>
      </AuthPageLayout>
    </PublicLayout>
  );
}
